/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert } from 'rsuite';
import { selectorDataPointsExhibitionType } from '../../../../../reducers';
import { RESET_DATAPOINTS_CACHE } from '../../../../../reducers/gerenciadorPrecos/datapointsCache';
import { selectorFiltersValues } from '../../../../../reducers/gerenciadorPrecos/filters/filtersValues';
import { selectorPriceManagerMoreFiltersValues } from '../../../../../reducers/gerenciadorPrecos/filters/moreFilters';
import { selectorQuickActionFilters } from '../../../../../reducers/gerenciadorPrecos/quickActionsFilter';
import { RESET_SELECTED_DATAPOINTS, SET_SELECTED_ALL, selectorSelectedDatapoints } from '../../../../../reducers/gerenciadorPrecos/selectedDatapoints';
import { selectorSort } from '../../../../../reducers/gerenciadorPrecos/sort';
import { selectorTotalElements } from '../../../../../reducers/gerenciadorPrecos/totalElements';
import { downloadFile, downloadSelectedDatapoints, generateDownloadFileName } from '../../services_ts';
import {
  GET_MODEL_GERENCIADOR_FILTROS,
  GET_MODEL_GERENCIADOR_QUICK_ACTION_FILTERS,
  isSelectedDatapointsEmpty as fnIsEmptySelectedDatapoints,
  getTotalProducts,
  isEmptyFilters,
} from '../../utils';

const useGerenciadorTableHeading = () => {
  const dispatch = useDispatch();
  const [isDownloadLoading, setIsDownloadLoading] = useState(false);

  const totalElements = useSelector(selectorTotalElements);

  const filters = useSelector(selectorFiltersValues);
  const moreFilters = useSelector(selectorPriceManagerMoreFiltersValues);

  const quickActionsFilter = useSelector(selectorQuickActionFilters);
  const sort = useSelector(selectorSort);

  const selectedDatapoints = useSelector(selectorSelectedDatapoints);
  const datapointExhibitionType = useSelector(selectorDataPointsExhibitionType);

  const { selectedAll, selectedIds, excludedIds } = selectedDatapoints;

  const MODEL = useMemo(
    () => ({
      ...GET_MODEL_GERENCIADOR_FILTROS(filters),
      ...GET_MODEL_GERENCIADOR_QUICK_ACTION_FILTERS(quickActionsFilter, sort.priceType),
      ...moreFilters,
    }),
    [filters, quickActionsFilter, moreFilters, sort.priceType],
  );

  const IS_EMPTY_FILTERS = useMemo(() => isEmptyFilters(MODEL), [MODEL]);

  const handleSelectAllFromAllPages = useCallback(() => {
    dispatch(SET_SELECTED_ALL(true));
  }, [dispatch]);

  const handleUnselectAllFromAllPages = useCallback(() => {
    dispatch(RESET_SELECTED_DATAPOINTS());
    dispatch(RESET_DATAPOINTS_CACHE());
  }, [dispatch]);

  const handleDownloadSelectedData = useCallback(async () => {
    try {
      setIsDownloadLoading(true);

      const filtersModel = {
        ...GET_MODEL_GERENCIADOR_FILTROS(filters),
        ...GET_MODEL_GERENCIADOR_QUICK_ACTION_FILTERS(quickActionsFilter, sort.priceType),
        ...moreFilters,
        sort: 'productName,asc',
        page: 0,
        size: 25,
      };

      const response = await downloadSelectedDatapoints({
        selectedIds,
        excludedIds,
        selectedAll,
        datapointExhibitionType,
        filters: filtersModel as any,
      });

      if (response.status === 200) {
        const filename = generateDownloadFileName();
        const blob = new Blob([response.data]);
        downloadFile(blob, filename);
        Alert.success('Download realizado com sucesso');
      } else if (response.status === 202) {
        Alert.info('Sua solicitação está sendo processada e será enviada por email, por favor, aguarde uns instantes.', 5000);
      }
    } catch (error: any) {
      console.error('Download error:', error);

      let errorMessage = 'Erro ao realizar download';

      if (error?.response?.data) {
        try {
          let responseData = error.response.data;

          if (responseData instanceof ArrayBuffer) {
            const decoder = new TextDecoder('utf-8');
            responseData = JSON.parse(decoder.decode(responseData));
          }

          if (responseData?._embedded?.errors?.[0]?.message) {
            errorMessage = responseData._embedded.errors[0].message;
          } else if (responseData?.embedded?.errors?.[0]?.message) {
            errorMessage = responseData.embedded.errors[0].message;
          } else if (responseData?.message) {
            errorMessage = responseData.message;
          } else if (typeof responseData === 'string') {
            errorMessage = responseData;
          }
        } catch (parseError) {
          console.error('Erro ao fazer parse da mensagem de erro:', parseError);
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }

      Alert.error(errorMessage);
    } finally {
      setIsDownloadLoading(false);
    }
  }, [selectedIds, excludedIds, selectedAll, datapointExhibitionType, filters, quickActionsFilter, moreFilters, sort.priceType]);

  const isEmptySelectedDatapoints = useMemo(() => fnIsEmptySelectedDatapoints(selectedDatapoints), [selectedDatapoints]);

  const total = useMemo(() => getTotalProducts(totalElements, selectedDatapoints), [totalElements, selectedDatapoints]);

  return {
    total,
    isFiltersEmpty: IS_EMPTY_FILTERS,
    selectedAll,
    isEmptySelectedDatapoints,
    handleSelectAllFromAllPages,
    handleUnselectAllFromAllPages,
    handleDownloadSelectedData,
    isDownloadLoading,
  };
};

export default useGerenciadorTableHeading;
